/**
 * Lightweight, fire-and-forget client for the Market Creation funnel.
 * - Generates a stable anonymous session id in localStorage so logged-out
 *   visitors can still be counted as the top of the funnel.
 * - Never throws; network/validation errors are swallowed so tracking can't
 *   impact the user experience.
 */

const SESSION_KEY = 'amp_funnel_session_id';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const newId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

export const getFunnelSessionId = () => {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable — return a per-call id so the event still lands.
    return newId();
  }
};

/**
 * Track a funnel event. Never awaits; never throws.
 * @param {'banner_click'|'auth_shown'|'auth_success'|'market_submitted'} eventType
 * @param {{ source?: string, userId?: string }} meta
 */
export const trackFunnelEvent = (eventType, meta = {}) => {
  try {
    const body = JSON.stringify({
      event_type: eventType,
      session_id: getFunnelSessionId(),
      source: meta.source || null,
      user_id: meta.userId || null,
    });
    // keepalive lets this fire during navigation without being cancelled.
    fetch(`${API_URL}/api/analytics/funnel/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // ignore — analytics must never break the app.
  }
};
